import type { HrcConnectionPayload, StitchConnectionData } from './types'

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/** Non-string entries are coerced (not dropped) so the backend sees everything the vendor granted. */
function normalizeScope(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    return nonEmptyString(
      value
        .filter((v) => v !== null && v !== undefined)
        .map(String)
        .join(' ')
    )
  }
  return nonEmptyString(value)
}

/** Case-insensitive: the vendor's "LIVE" is still live. Unknown values are omitted (never guessed). */
function normalizeApiMode(value: unknown): 'test' | 'live' | undefined {
  const mode = typeof value === 'string' ? value.trim().toLowerCase() : undefined
  return mode === 'test' || mode === 'live' ? mode : undefined
}

/**
 * Map the vendor's snake_case connection to the BFF's camelCase contract.
 * Returns null when there is no usable `org_connection_id` (the caller must then NOT contact the backend).
 * Absent optional fields are omitted — notably `consentExpiresAt`, which must never be invented.
 */
export function toConnectionPayload(data: StitchConnectionData | undefined): HrcConnectionPayload | null {
  const orgConnectionId = nonEmptyString(data?.org_connection_id)
  if (!data || !orgConnectionId) return null

  const optional: Omit<HrcConnectionPayload, 'orgConnectionId'> = {
    endpointId: nonEmptyString(data.endpoint_id),
    brandId: nonEmptyString(data.brand_id),
    portalId: nonEmptyString(data.portal_id),
    platformType: nonEmptyString(data.platform_type),
    connectionStatus: nonEmptyString(data.connection_status),
    requestId: nonEmptyString(data.request_id),
    scope: normalizeScope(data.scope),
    consentExpiresAt: nonEmptyString(data.consent_expires_at),
    apiMode: normalizeApiMode(data.api_mode),
  }
  const defined = Object.fromEntries(Object.entries(optional).filter(([, v]) => v !== undefined))
  return { orgConnectionId, ...defined }
}
